import { Locales } from '@porterin/component-library/src/ts-utils/analytics/constants/LocaleConstants';

import {
  FareEstimateFormParameter,
  GTESPayload,
  QueryMapperResult,
} from '../types/HelperTypes';
import { formatName } from '@porterin/booking-flow/src/common/domain/helpers/CommonBookingFlowHelpers';

export const queryMapper = (
  routeQuery: Record<string, string | undefined>,
): QueryMapperResult => {
  return {
    from_address_landmark: routeQuery['from_address_landmark'],
    from_address_lat: routeQuery['from_address_lat'],
    from_address_long: routeQuery['from_address_long'],
    to_address_landmark: routeQuery['to_address_landmark'],
    to_address_lat: routeQuery['to_address_lat'],
    to_address_long: routeQuery['to_address_long'],
    geo_region_id: routeQuery['geo_region_id'],
    sender_name:
      formatName(
        routeQuery['sender_name'] ?? routeQuery['customer_name'] ?? '',
      ) || undefined,
    sender_mobile: routeQuery['sender_mobile'] ?? routeQuery['customer_mobile'],
    customer_name: formatName(routeQuery['customer_name'] ?? '') || undefined,
    customer_mobile: routeQuery['customer_mobile'],
    frequency: routeQuery['frequency'],
    brand: routeQuery['brand'],
    is_device_mobile: routeQuery['is_device_mobile'],
    fare_estimate_token: routeQuery['fare_estimate_token'],
    service_category: routeQuery['service_category'],
    utm_source: routeQuery['utm_source'],
    utm_medium: routeQuery['utm_medium'],
    utm_campaign: routeQuery['utm_campaign'],
    utm_content: routeQuery['utm_content'],
    utm_term: routeQuery['utm_term'],
    vehicle_type: routeQuery['vehicle_type'],
    result: routeQuery['result'],
    prev_callback_req_id_present: routeQuery['prev_callback_req_id_present'],
    callback_request_id: routeQuery['callback_request_id'],
    is_existing_cust: routeQuery['is_existing_cust'],
    lead_entry_created: routeQuery['lead_entry_created'],
  };
};

export const FareEstimateFormConfig = {
  required_length: 1,
  name_length: 3,
  mobile_length: 10,
};

export const FormValidationRegex = {
  name: /[a-zA-Z].*[a-zA-Z].*[a-zA-Z]/,
  mobile: /^[6-9]\d{9}$/,
};

export const API = {
  fare_estimate_v2: 'website/fare_estimate_validator_v2',
  fare_estimate_secure: 'fare_estimate_secure',
} as const;

// Appends query parameters to a URL, handling both cases where URL already has parameters or not
export const addParamsToUrl = (
  url: string,
  params: [string, string][],
): string => {
  const has_params = /.*\?{1}.+/.test(url);
  const params_string = params.reduce(
    (acc: string, [key, value], index: number) =>
      `${acc}${key}=${value}${index === params.length - 1 ? '' : '&'}`,
    has_params ? '&' : '?',
  );
  return `${url}${params_string}`;
};

export const validateNumber = (code: string): boolean => {
  return /^[0-9]+$/.test(code);
};

export function getAnchorElement(url: string): HTMLAnchorElement {
  const elem = document.createElement('a');
  elem.setAttribute('href', url);
  return elem;
}

export const getHostName = (url: string): string => {
  const elem = getAnchorElement(url);
  if (elem.port === '') {
    return elem.hostname;
  } else {
    return `${elem.hostname}:${elem.port}`;
  }
};

export const getPathSplitParts = (url: string): string[] => {
  const location = getAnchorElement(url).pathname.split('/');
  return location.slice(1); // Remove the first empty element and return the rest
};

export function getSearchParams(url: string): string {
  return getAnchorElement(url).search.substring(1);
}

export let getDefaultParamsValues = (source: string): string[] => [
  'website_fare_estimate_ssr',
  'default_' + source,
  'default_' + source,
  'default_' + source,
  '',
];

export const createRequestURLfrmParams = (
  hostName: string,
  requestUrl: string,
  urlParams: Record<string, string>,
): string => {
  const queryString = new URLSearchParams(urlParams);
  return queryString
    ? `${hostName}${requestUrl}?${queryString}`
    : `${hostName}${requestUrl}`;
};

export const getURLParams = (searchStr: string): Record<string, string> => {
  const urlParams: Record<string, string> = {};

  searchStr.split('&').forEach(param => {
    const params = param.split('=');
    if (params.length >= 2) {
      urlParams[params[0]] = params[1];
    }
  });

  return urlParams;
};

let utm_source = 'utm_source';
let utm_medium = 'utm_medium';
let utm_campaign = 'utm_campaign';
let utm_term = 'utm_term';
let utm_content = 'utm_content';

let utm_params_keys = [
  utm_source,
  utm_medium,
  utm_campaign,
  utm_term,
  utm_content,
];

export const getUtmKeyValues = (source: string): [string, string][] => {
  const url_params = getURLParams(getSearchParams(window.location.href));
  const default_param_values = getDefaultParamsValues(source);

  return utm_params_keys.map(
    (param: string, index: number): [string, string] => {
      const param_value = url_params[param] || '';

      if (param_value.length === 0) {
        return [param, default_param_values[index]];
      } else {
        return [param, param_value];
      }
    },
  );
};

export const getFareEstimateFormPayload = (
  params: FareEstimateFormParameter,
): GTESPayload => {
  return {
    GTES_mobile: params.mobile,
    GTES_city: params.user_city || '',
    GTES_category: params.service_category,
    GTES_parameter: `${params.user_city || ''}_${params.mobile}_${params.service_category}_${params.frequency}`,
    GTES_frequency: params.frequency,
  };
};

export const getGtesUnregisteredPayload = (
  params: FareEstimateFormParameter,
): Record<string, string> => {
  return {
    GTES_UR_mobile: params.mobile,
    GTES_UR_city: params.user_city || '',
    GTES_UR_category: params.service_category,
    GTES_UR_parameter:
      params.user_city ||
      '' +
        '_' +
        params.mobile +
        '_' +
        params.service_category +
        '_' +
        params.frequency,
    GTES_UR_frequency: params.frequency,
  };
};

export const BookingFlowEnabledLocales: Locales[] = [Locales.EN_IN] as const;

export const geoRegionIdToCity = (id: number): string => {
  if (process.env['APP_ENV'] === 'staging') {
    switch (id) {
      case 1:
        return 'mumbai';
      case 2:
        return 'delhi_NCR';
      case 3:
        return 'bangalore';
      case 10:
        return 'hyderabad';
      case 11:
        return 'chennai';
      case 6:
        return 'ahmedabad';
      case 19:
        return 'jaipur';
      case 20:
        return 'pune';
      case 71:
        return 'kolkata';
      case 72:
        return 'surat';
      case 73:
        return 'lucknow';
      case 12:
        return 'coimbatore';
      case 13:
        return 'indore';
      case 14:
        return 'nagpur';
      case 15:
        return 'chandigarh';
      case 16:
        return 'vadodara';
      case 17:
        return 'ludhiana';
      case 18:
        return 'kochi';
      case 31:
        return 'nashik';
      case 29:
        return 'visakhapatnam';
      case 30:
        return 'kanpur';
      default:
        return 'bangalore';
    }
  } else {
    switch (id) {
      case 1:
        return 'mumbai';
      case 2:
        return 'delhi_NCR';
      case 3:
        return 'bangalore';
      case 4:
        return 'hyderabad';
      case 5:
        return 'chennai';
      case 6:
        return 'ahmedabad';
      case 7:
        return 'jaipur';
      case 8:
        return 'pune';
      case 9:
        return 'kolkata';
      case 10:
        return 'surat';
      case 11:
        return 'lucknow';
      case 12:
        return 'coimbatore';
      case 13:
        return 'indore';
      case 14:
        return 'nagpur';
      case 15:
        return 'chandigarh';
      case 16:
        return 'vadodara';
      case 17:
        return 'ludhiana';
      case 18:
        return 'kochi';
      case 19:
        return 'nashik';
      case 29:
        return 'visakhapatnam';
      case 30:
        return 'kanpur';
      default:
        return 'bangalore';
    }
  }
};

export const defaultRequirement = 'personal';

export const SENDER_LOCAL_STORAGE_KEY = 'sender';
export const RECEIVER_LOCAL_STORAGE_KEY = 'receiver';
